import { ArrowRightOutlined, ExportOutlined } from '@ant-design/icons';
import { Checkbox, Drawer, Flex, Modal, Tag } from 'antd';
import Button from 'components/Button';
import * as React from 'react';
import { PhotoProvider, PhotoView } from 'react-photo-view';

import useBaseTableSingleRecordQuery from '../hooks/useBaseTableSingleRecordQuery';

import { renderDate, renderInfoValue } from 'utils/helpers';
import { AIRTABLE_COLOR_MAP } from '../../shared/config';
import { FieldMeta } from '../../shared/types';
import { isValidUrl } from '../../util';

interface Attachment {
  id: string;
  url: string;
  type: string;
  filename: string;
}

interface Choice {
  name: string;
  color: string;
  id: string;
}

interface FieldRendererProps {
  name: string;
  value: string;
  fieldMeta: Record<string, FieldMeta> | null;
}

const SelectTag: React.FC<{ value: string; choices?: Choice[] }> = ({
  value,
  choices,
}) => {
  const choice = choices?.find(
    (c) => c?.name?.toLowerCase() === value?.toLowerCase(),
  );

  if (!choice) return <>N/A</>;

  return (
    <Tag
      color={
        AIRTABLE_COLOR_MAP[choice?.color as keyof typeof AIRTABLE_COLOR_MAP] ||
        '#c4ecff'
      }
      bordered={false}
      style={{ borderRadius: 20, color: 'black' }}
    >
      {value}
    </Tag>
  );
};

const AttachmentRenderer: React.FC<{ attachments: Attachment[] }> = ({
  attachments,
}) => (
  <Flex gap={1} wrap="wrap">
    {!attachments?.length && <>No attachments</>}
    {attachments?.map((item) =>
      item.type?.includes('image') ? (
        <PhotoProvider key={item.id}>
          <PhotoView src={item.url}>
            <img
              title="View attachment"
              src={item.url}
              alt="user"
              height={80}
              width={80}
              className="mb-4 rounded me-2"
            />
          </PhotoView>
        </PhotoProvider>
      ) : (
        <a
          href={item.url}
          key={item.id}
          title="View attachment"
          target="_blank"
          rel="noopener noreferrer"
        >
          {item?.filename}
        </a>
      ),
    )}
  </Flex>
);

const LinkedRecordModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  name: string;
  data: Record<string, any>;
}> = ({ isOpen, onClose, name, data }) => (
  <Modal
    title={name}
    open={isOpen}
    onCancel={onClose}
    afterClose={onClose}
    footer={null}
    style={{ padding: 10 }}
  >
    <Flex vertical gap={20} className="mt-4">
      {Object.entries(data)
        ?.filter(([_, value]) => !Array.isArray(value))
        ?.map(([key, value]) => (
          <Flex gap={2} vertical key={key}>
            <b>{key}:</b>
            <FieldRenderer
              name={key}
              value={value as string}
              fieldMeta={null}
            />
          </Flex>
        ))}
    </Flex>
  </Modal>
);

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  name,
  value,
  fieldMeta,
}) => {
  const [isLinkOpen, setIsLinkOpen] = React.useState(false);
  const [isLinkedFieldDrawerOpen, setIsLinkedFieldDrawerOpen] =
    React.useState(false);

  const fieldConfig = fieldMeta?.[name];
  const fieldType = fieldConfig?.type;

  const { data: linkedRecordData } = useBaseTableSingleRecordQuery(
    fieldConfig?.args?.[0] as string,
    fieldConfig?.args?.[1] as string,
    Array.isArray(value) ? value[0] : undefined,
    Boolean(fieldConfig?.args?.[0] && fieldConfig?.args?.[1] && value),
  );

  const fieldTypeRenderers = React.useMemo(
    () => ({
      singleSelect: () => (
        <SelectTag
          value={value as string}
          choices={fieldConfig?.options?.choices}
        />
      ),
      multipleAttachments: () => (
        <AttachmentRenderer attachments={value as unknown as Attachment[]} />
      ),
      checkbox: () => <Checkbox checked={!!value} className="ms-5" />,
      date: () => renderDate(value, 'N/A', 'DD MMM YYYY'),
      multipleRecordLinks: () =>
        linkedRecordData ? (
          <Flex align="center" gap={4}>
            <Button
              type="link"
              size="small"
              className="d-flex align-items-center m-0 p-0"
              onClick={() => setIsLinkedFieldDrawerOpen(true)}
            >
              {Object.entries(linkedRecordData)
                ?.filter(([_, value]) => !Array.isArray(value))
                ?.slice(0, 1)
                ?.map(([key, value]) => (
                  <Flex gap={2} vertical key={key}>
                    <FieldRenderer
                      name={key}
                      value={value as string}
                      fieldMeta={null}
                    />
                  </Flex>
                ))}
            </Button>
            <LinkedRecordModal
              isOpen={isLinkedFieldDrawerOpen}
              onClose={() => setIsLinkedFieldDrawerOpen(false)}
              name={name}
              data={linkedRecordData}
            />
          </Flex>
        ) : (
          'N/A'
        ),
    }),
    [isLinkedFieldDrawerOpen, linkedRecordData, fieldConfig, value, name],
  );

  if (typeof value === 'string' && isValidUrl(value)) {
    return value?.includes('embed') ? (
      <>
        <Button
          role="button"
          className="d-flex align-items-center m-0 p-0"
          type="link"
          onClick={() => setIsLinkOpen(true)}
          trackerLabel={
            name?.toLowerCase().includes('update') ? 'Edit' : 'Open Link'
          }
        >
          {name?.toLowerCase().includes('update') ? 'Edit' : 'Open Link'}
          <ArrowRightOutlined />
        </Button>
        <Drawer
          open={isLinkOpen}
          onClose={() => setIsLinkOpen(false)}
          footer={null}
          style={{ padding: 10 }}
          width={720}
          styles={{ body: { padding: 0, margin: 0 } }}
        >
          <iframe
            className="airtable-embed"
            src={value}
            width="100%"
            height="100%"
            style={{ background: 'transparent' }}
            title="Embedded content"
          />
        </Drawer>
      </>
    ) : (
      <a href={value} target="_blank" rel="noopener noreferrer">
        Open in new tab <ExportOutlined />
      </a>
    );
  }

  const renderer =
    fieldTypeRenderers[fieldType as keyof typeof fieldTypeRenderers];
  if (renderer) {
    return renderer();
  }

  return renderInfoValue(value?.toString(), 'N/A');
};

export default FieldRenderer;
